import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "../../services/toast";
import { useNavigate } from "react-router-dom";
import Markdown from "markdown-to-jsx";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, Typography, Tabs, Tab, Paper, Tooltip, Chip } from "@mui/material";
import { PROMPT_TYPES } from "../../helpers/promptTypes";
import PromptTestChat from "../PromptTestChat";
import PromptVersionHistory from "../PromptVersionHistory";
import PromptKnowledgeTab from "../PromptKnowledgeTab";
import ToolHeadersEditor from "../ToolHeadersEditor";
import {
    Visibility,
    VisibilityOff,
    DeleteOutlined,
    EditOutlined,
    AddOutlined,
    SettingsOutlined,
    NotesOutlined,
    HistoryOutlined,
    AccountTreeOutlined,
    BuildOutlined,
    OpenInFullOutlined,
    StorageOutlined,
    LanguageOutlined,
    EventOutlined,
    MenuBookOutlined,
    InfoOutlined,
    AutoAwesomeOutlined,
} from "@mui/icons-material";
import PromptBuilderDialog from "./PromptBuilderDialog";
import { InputAdornment, IconButton, Switch, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const VOICE_ACCENT_OPTIONS = [
    { value: "", label: "Neutro" },
    { value: "ecuador", label: "Ecuador" },
    { value: "mexico", label: "México" },
    { value: "colombia", label: "Colombia" },
    { value: "argentina", label: "Argentina" },
    { value: "espana", label: "España" },
    { value: "usa", label: "Estados Unidos (español)" },
];

const TEXT_MODELS = [
    { value: "", label: "Por defecto (gpt-4o-mini)" },
    { value: "gpt-4o-mini", label: "gpt-4o-mini · rápido y barato" },
    { value: "gpt-4o", label: "gpt-4o · más capaz" },
    { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
    { value: "gpt-4.1", label: "gpt-4.1" },
    { value: "gpt-6-luna", label: "gpt-6-luna · candidato, en prueba" },
    { value: "gpt-6-sol", label: "gpt-6-sol · candidato, en prueba" },
];

const insertTab = (e) => {
    if (e.key !== "Tab" || e.shiftKey) return;
    e.preventDefault();

    const field = e.target;
    const { selectionStart: from, selectionEnd: to, value } = field;
    field.value = `${value.slice(0, from)}  ${value.slice(to)}`;
    field.selectionStart = field.selectionEnd = from + 2;

    field.dispatchEvent(new Event("input", { bubbles: true }));
};

const PromptLineNumbers = ({ value, textareaRef, className }) => {
    const gutterRef = useRef(null);
    const lineCount = Math.max(1, (value || "").split("\n").length);

    useEffect(() => {
        const textarea = textareaRef.current;
        const gutter = gutterRef.current;
        if (!textarea || !gutter) return undefined;

        const sync = () => { gutter.scrollTop = textarea.scrollTop; };
        sync();
        textarea.addEventListener("scroll", sync);
        return () => textarea.removeEventListener("scroll", sync);
    }, [textareaRef, value]);

    return (
        <div ref={gutterRef} className={className} aria-hidden="true">
            {Array.from({ length: lineCount }, (_, i) => (
                <div key={i}>{i + 1}</div>
            ))}
        </div>
    );
};

const countText = (value) => {
    const text = value || "";
    const lines = text ? text.split("\n").length : 0;
    return `${lines} línea${lines === 1 ? "" : "s"} · ${text.length} caracteres`;
};

const useStyles = makeStyles()(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    inlineRoot: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 360px",
        gap: theme.spacing(2),
        height: "100%",
        minHeight: 0,
        [theme.breakpoints.down("lg")]: {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "minmax(560px, 1fr) 520px",
            overflowY: "auto",
        },
    },
    inlinePanel: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        "& form": {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
        },
        "& form > .MuiDialogContent-root": {
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
        },
    },
    inlineTabPanel: {
        flex: 1,
        minHeight: 0,
        height: "auto",
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    inlineBasicTab: {
        overflow: "hidden",
    },
    inlineFill: {
        height: "100%",
        minHeight: 0,
        alignItems: "stretch",
    },
    inlineEditorRow: {
        flex: 1,
        minHeight: 0,
        "& .MuiFormControl-root": {
            height: "100%",
            margin: 0,
        },
        "& .MuiInputBase-root": {
            height: "100%",
            alignItems: "flex-start",
        },
        "& textarea": {
            height: "100% !important",
            overflowY: "auto !important",
            ...theme.scrollbarStyles,
        },
    },
    inlineSettings: {
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        paddingRight: theme.spacing(0.5),
        ...theme.scrollbarStyles,
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    tabs: {
        marginBottom: theme.spacing(2),
        minHeight: 38,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "& .MuiTab-root": {
            minHeight: 38,
            minWidth: 0,
            padding: theme.spacing(0, 2),
            fontSize: 13,
            fontWeight: 500,
            textTransform: "none",
            letterSpacing: 0,
        },
        "& .MuiTab-iconWrapper": {
            marginRight: 6,
            fontSize: 17,
        },
        "& .MuiTabs-indicator": {
            height: 2,
        },
    },
    tabPanel: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        height: 620,
        overflowY: "auto",
        overflowX: "hidden",
        "&[hidden]": {
            display: "none",
        },
    },
    basicoGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 260px",
        gap: theme.spacing(3),
        alignItems: "start",
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
        },
    },
    activeToggle: {
        flexShrink: 0,
        height: 34,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.25),
        padding: theme.spacing(0, 0.25, 0, 1.5),
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: "nowrap",
        color: theme.palette.text.primary,
        cursor: "pointer",
        userSelect: "none",
        transition: "border-color .15s ease",
        "&:hover": { borderColor: theme.palette.text.secondary },
    },
    activeToggleOff: {
        color: theme.palette.error.main,
    },
    inlineHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    inlineName: {
        flex: 1,
        minWidth: 0,
        "& .MuiInputBase-root": {
            fontSize: 20,
            fontWeight: 600,
            borderRadius: 8,
            padding: theme.spacing(0.25, 1),
            border: "1px solid transparent",
            transition: "border-color .15s ease",
            "&:hover": { borderColor: theme.palette.divider },
            "&.Mui-focused": { borderColor: theme.palette.primary.main },
        },
        "& input": { padding: theme.spacing(0.5, 0) },
    },
    versionChip: {
        flexShrink: 0,
        height: 34,
        borderRadius: 999,
        borderColor: theme.palette.divider,
        fontSize: 13,
        fontWeight: 500,
        transition: "border-color .15s ease",
        "& .MuiChip-icon": { marginLeft: theme.spacing(1.25), fontSize: 17 },
        "& .MuiChip-label": { paddingLeft: theme.spacing(0.75), paddingRight: theme.spacing(1.5) },
        "&:hover": { borderColor: theme.palette.text.secondary },
    },
    versionNote: {
        flex: 1,
        marginRight: theme.spacing(1),
        maxWidth: 520,
    },
    inlineType: {
        flexShrink: 0,
        height: 34,
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1.5),
        fontSize: 13,
        fontWeight: 500,
        transition: "border-color .15s ease",
        "& .MuiSelect-select": {
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: "32px !important",
            lineHeight: "32px",
        },
        "& .MuiSelect-icon": { right: 8 },
        "&:hover": { borderColor: theme.palette.text.secondary },
        "&.Mui-focused": { borderColor: theme.palette.primary.main },
    },
    settingsList: {
        maxWidth: 900,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        marginTop: theme.spacing(1),
    },
    settingRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 300px",
        gap: theme.spacing(3),
        alignItems: "center",
        padding: theme.spacing(1.75, 2.5),
        "& + &": { borderTop: `1px solid ${theme.palette.divider}` },
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
            gap: theme.spacing(1),
        },
    },
    settingTitle: {
        fontSize: 14,
        fontWeight: 600,
    },
    settingHelp: {
        fontSize: 12.5,
        color: theme.palette.text.secondary,
        marginTop: 2,
        lineHeight: 1.45,
    },
    settingControl: {
        minWidth: 0,
        display: "flex",
        justifyContent: "flex-end",
    },
    settingNumber: {
        width: 140,
    },
    basicoColumna: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    promptEditor: {
        "& textarea": {
            fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
            fontSize: 13.5,
            lineHeight: 1.65,
            tabSize: 2,
            paddingLeft: 40,
        },
    },
    sqlEditor: {
        "& textarea": {
            fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
            fontSize: 13,
            lineHeight: 1.6,
        },
    },
    sourcePicker: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 0),
    },
    sourceOption: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        width: "100%",
        padding: theme.spacing(1.5),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        background: "transparent",
        color: "inherit",
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            background: theme.palette.mode === "light" ? "rgba(0,0,0,.02)" : "rgba(255,255,255,.03)",
        },
    },
    sourceIcon: {
        flex: "0 0 auto",
        color: theme.palette.text.secondary,
    },
    sourceName: {
        display: "block",
        fontWeight: 600,
        fontSize: 14,
    },
    sourceHint: {
        display: "block",
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    toolsBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    toolForm: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        padding: theme.spacing(1, 0),
    },
    toolSection: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    toolTitle: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    toolTitleSource: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontSize: 13,
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
    promptEditorBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: -theme.spacing(0.5),
    },
    promptEditorCount: {
        fontFamily: "ui-monospace, Consolas, monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
    },
    promptConIndice: {
        display: "flex",
        gap: theme.spacing(1.5),
        alignItems: "flex-start",
    },
    promptSplit: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },
    promptPaneLabel: {
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    promptPreviewCell: {
        position: "relative",
        minHeight: 420,
    },
    promptPreview: {
        position: "absolute",
        inset: 0,
        overflowY: "auto",
        padding: theme.spacing(1.5, 2),
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: 14,
        lineHeight: 1.6,
        "& h1": { fontSize: 18, fontWeight: 700, margin: theme.spacing(2, 0, 1) },
        "& h2": { fontSize: 15.5, fontWeight: 700, margin: theme.spacing(1.75, 0, 0.75) },
        "& h3": { fontSize: 14.5, fontWeight: 700, margin: theme.spacing(1.5, 0, 0.5) },
        "& h1:first-of-type": { marginTop: 0 },
        "& p": { margin: theme.spacing(0, 0, 1) },
        "& ul, & ol": { margin: theme.spacing(0, 0, 1), paddingLeft: theme.spacing(2.5) },
        "& li": { marginBottom: theme.spacing(0.25) },
        "& code": {
            fontFamily: "ui-monospace, Consolas, monospace",
            fontSize: 12.5,
            padding: "1px 4px",
            borderRadius: 3,
            background: theme.palette.action.hover,
        },
    },
    promptPreviewEmpty: {
        color: theme.palette.text.disabled,
        fontStyle: "italic",
    },
    promptEditorColumna: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
        flex: "1 1 auto",
        minWidth: 0,
    },
    promptEditorRow: {
        position: "relative",
        minWidth: 0,
    },
    promptLineNumbers: {
        position: "absolute",
        top: 1,
        left: 1,
        bottom: 1,
        width: 34,
        zIndex: 1,
        overflow: "hidden",
        textAlign: "right",
        userSelect: "none",
        pointerEvents: "none",
        borderRadius: "4px 0 0 4px",
        background: theme.palette.background.paper,
        fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
        fontSize: 13.5,
        lineHeight: 1.65,
        color: theme.palette.text.disabled,
        padding: "16.5px 8px 16.5px 0",
        boxSizing: "border-box",
    },
    sectionHelp: {
        display: "block",
        marginBottom: theme.spacing(1),
    },
    grupo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.25),
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        "&::after": {
            content: '""',
            flex: 1,
            height: 1,
            background: theme.palette.divider,
        },
    },
    herramientaCard: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
    },
    parametersBox: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.75),
    },
    herramientaHeader: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(0.5),
    },
    herramientaUrl: {
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 11,
        wordBreak: "break-all",
        display: "block",
    },
    paramTable: {
        tableLayout: "fixed",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        borderCollapse: "separate",
        overflow: "hidden",
        "& th": {
            fontSize: 11.5,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: theme.palette.text.secondary,
            background: theme.palette.action.hover,
            padding: theme.spacing(0.75, 1),
        },
        "& td": {
            padding: theme.spacing(0.75, 1),
            verticalAlign: "top",
        },
        "& tbody tr:last-of-type td": {
            borderBottom: 0,
        },
    },
    paramMainRow: {
        "& td": {
            borderBottom: 0,
            paddingBottom: 0,
        },
    },
    paramDetailRow: {
        "& td": {
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(1.25),
        },
    },
    paramAuto: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        padding: theme.spacing(1, 0.5),
    },
    toolEmpty: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    toolSectionHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.25),
        marginBottom: theme.spacing(1.5),
    },
    toolSectionNumber: {
        width: 22,
        height: 22,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.primary.contrastText,
        background: theme.palette.primary.main,
    },
    toolSectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        fontSize: 14,
        fontWeight: 700,
        lineHeight: "22px",
    },
    toolSectionBody: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
        paddingLeft: theme.spacing(4.25),
    },
    toolSubsection: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    toolSubsectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        fontSize: 12.5,
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    filterRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 160px",
        gap: theme.spacing(1),
        alignItems: "start",
    },
    vectorRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: theme.spacing(1),
        alignItems: "start",
    },
}));

const PARAMETER_FORMATS = [
    { value: "", label: "Sin validar" },
    { value: "nationalId", label: "Cédula (10 dígitos)" },
    { value: "mobilePhone", label: "Celular (09 + 8 dígitos)" },
    { value: "digits", label: "Sólo números" },
    { value: "email", label: "Correo electrónico" },
];

const splitPath = (path) => {
    const value = String(path || "");
    const queryIndex = value.indexOf("?");

    return queryIndex < 0 ? [value, ""] : [value.slice(0, queryIndex), value.slice(queryIndex + 1)];
};

const joinPath = (collection, filters) => (filters ? `${collection}?${filters}` : collection);

const parseFilters = (filters) => {
    const columns = [];
    let limit = "";
    const rest = [];

    for (const pair of String(filters || "").split("&").filter(Boolean)) {
        if (pair.startsWith("select=")) {
            columns.push(...pair.slice(7).split(",").filter(Boolean));
        } else if (pair.startsWith("limit=")) {
            limit = pair.slice(6);
        } else {
            rest.push(pair);
        }
    }

    return { columns, limit, rest: rest.join("&") };
};

const buildFilters = ({ columns, limit, rest }) => {
    const lista = Array.isArray(columns) ? columns : String(columns || "").split(",").filter(Boolean);

    return [
        lista.length ? `select=${lista.join(",")}` : "",
        limit ? `limit=${limit}` : "",
        rest || "",
    ]
        .filter(Boolean)
        .join("&");
};

const SOURCE_KIND_LABELS = {
    supabase: "Supabase · PostgREST",
    rest: "API REST",
    postgres: "PostgreSQL directo",
    mongo: "MongoDB",
};

const sourceLabel = (tool, dataSources) => {
    if (tool.kind !== "database") return "HTTP / REST · una URL propia";

    const source = dataSources.find(s => String(s.id) === String(tool.dataSourceId));
    if (!source) return "Sin conexión";

    return `${source.name} · ${SOURCE_KIND_LABELS[source.kind] || source.kind}`;
};

const SourcePicker = ({ dataSources, onPick, onPickCalendar, classes }) => (
    <div className={classes.sourcePicker}>
        {dataSources.map(source => (
            <button
                key={source.id}
                type="button"
                className={classes.sourceOption}
                onClick={() =>
                    onPick({
                        kind: "database",
                        dataSourceId: source.id,
                        method: "GET",
                        url: "",
                    })
                }
            >
                <StorageOutlined className={classes.sourceIcon} />
                <span>
                    <span className={classes.sourceName}>{source.name}</span>
                    <span className={classes.sourceHint}>
                        {SOURCE_KIND_LABELS[source.kind] || source.kind}
                    </span>
                </span>
            </button>
        ))}

        <button
            type="button"
            className={classes.sourceOption}
            onClick={() =>
                onPick({ kind: "webhook", dataSourceId: "", method: "POST", path: "", query: "" })
            }
        >
            <LanguageOutlined className={classes.sourceIcon} />
            <span>
                <span className={classes.sourceName}>HTTP / REST</span>
                <span className={classes.sourceHint}>
                    Una URL propia — n8n, un webhook, una API externa
                </span>
            </span>
        </button>

        <button
            type="button"
            className={classes.sourceOption}
            onClick={onPickCalendar}
        >
            <EventOutlined className={classes.sourceIcon} />
            <span>
                <span className={classes.sourceName}>Google Calendar</span>
                <span className={classes.sourceHint}>
                    Ver disponibilidad y agendar citas
                </span>
            </span>
        </button>

        {dataSources.length === 0 && (
            <Typography variant="caption" color="textSecondary">
                {i18n.t("promptModal.form.herramientas.noConnections")}
            </Typography>
        )}
    </div>
);

const SourceEditor = ({ tool, dataSources, onChange, classes }) => {
    const [collections, setCollections] = useState([]);
    const dataSourceId = tool.dataSourceId || "";
    const [collection, filters] = splitPath(tool.path);

    const collectionOptions =
        collection && !collections.includes(collection) ? [collection, ...collections] : collections;

    const kind = tool.kind || (tool.dataSourceId ? "database" : "webhook");

    const selected = dataSources.find(source => String(source.id) === String(dataSourceId));
    const isSql = selected?.kind === "postgres";
    const isMongo = selected?.kind === "mongo";
    const isVector = Boolean(tool.vectorParam);

    const parsed = parseFilters(filters);
    const [columnOptions, setColumnOptions] = useState([]);

    useEffect(() => {
        if (!dataSourceId || !collection) {
            setColumnOptions([]);
            return undefined;
        }

        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(
                    `/data-sources/${dataSourceId}/collections/${encodeURIComponent(collection)}/columns`
                );
                if (alive) setColumnOptions(data || []);
            } catch (err) {
                if (alive) setColumnOptions([]);
            }
        })();

        return () => {
            alive = false;
        };
    }, [dataSourceId, collection]);

    useEffect(() => {
        if (!dataSourceId) {
            setCollections([]);
            return;
        }

        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/data-sources/${dataSourceId}/collections`);
                if (alive) setCollections(data || []);
            } catch (err) {
                if (alive) setCollections([]);
            }
        })();

        return () => {
            alive = false;
        };
    }, [dataSourceId]);

    return (
        <div className={classes.toolSection}>
            {}
            {kind === "database" ? (
                <>
                    {}
                    {}
                    {!isSql && !isMongo && (
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            fullWidth
                            label={i18n.t("promptModal.form.herramientas.searchMode")}
                            value={isVector ? "vector" : "text"}
                            onChange={e =>
                                onChange(
                                    e.target.value === "vector"
                                        ? {
                                              vectorParam: "query_embedding",
                                              embeddingModel: "text-embedding-3-small",
                                              method: "POST",
                                              path: "rpc/",
                                          }
                                        : { vectorParam: "", embeddingModel: "", method: "GET", path: "" }
                                )
                            }
                        >
                            <MenuItem value="text">
                                {i18n.t("promptModal.form.herramientas.searchText")}
                            </MenuItem>
                            <MenuItem value="vector">
                                {i18n.t("promptModal.form.herramientas.searchVector")}
                            </MenuItem>
                        </TextField>
                    )}

                    {isVector && (
                        <>
                            <TextField
                                size="small"
                                variant="outlined"
                                fullWidth
                                label={i18n.t("promptModal.form.herramientas.rpc")}
                                placeholder="rpc/match_agencies"
                                slotProps={infoSlot(i18n.t("promptModal.form.herramientas.rpcHelp"))}
                                value={tool.path || ""}
                                onChange={e => onChange({ path: e.target.value })}
                            />

                            {}
                            <div className={classes.vectorRow}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    label={i18n.t("promptModal.form.herramientas.vectorParam")}
                                    slotProps={infoSlot(i18n.t("promptModal.form.herramientas.vectorParamHelp"))}
                                    value={tool.vectorParam || ""}
                                    onChange={e => onChange({ vectorParam: e.target.value })}
                                />
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    label={i18n.t("promptModal.form.herramientas.embeddingModel")}
                                    slotProps={infoSlot(i18n.t("promptModal.form.herramientas.embeddingWarning"), { warning: true })}
                                    value={tool.embeddingModel || ""}
                                    onChange={e => onChange({ embeddingModel: e.target.value })}
                                />
                            </div>
                        </>
                    )}

                    {isSql ? (
                        <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={12}
                            className={classes.sqlEditor}
                            label={i18n.t("promptModal.form.herramientas.query")}
                            placeholder={"select nombre, direccion, telefono\nfrom oficinas\nwhere ciudad ilike :ciudad\nlimit 5"}
                            slotProps={infoSlot(i18n.t("promptModal.form.herramientas.queryHelp"))}
                            value={tool.query || ""}
                            onChange={e => onChange({ query: e.target.value })}
                        />
                    ) : isVector ? null : (
                        <>
                            <TextField
                                select={collectionOptions.length > 0}
                                size="small"
                                variant="outlined"
                                fullWidth
                                disabled={!dataSourceId}
                                label={i18n.t("promptModal.form.herramientas.collection")}
                                slotProps={
                                    collectionOptions.length
                                        ? undefined
                                        : infoSlot(i18n.t("promptModal.form.herramientas.collectionNoList"))
                                }
                                value={collection}
                                onChange={e => onChange({ path: joinPath(e.target.value, filters) })}
                            >
                                {collectionOptions.map(nombre => (
                                    <MenuItem key={nombre} value={nombre}>
                                        {nombre}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <div className={classes.filterRow}>
                                {}
                                {columnOptions.length > 0 ? (
                                    <TextField
                                        select
                                        slotProps={infoSlot(i18n.t("promptModal.form.herramientas.columnsHelp"), {
                                            select: {
                                                multiple: true,
                                                renderValue: elegidas => elegidas.join(", "),
                                            },
                                        })}
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        disabled={!collection}
                                        label={i18n.t("promptModal.form.herramientas.columns")}
                                        value={parsed.columns}
                                        onChange={e =>
                                            onChange({
                                                path: joinPath(
                                                    collection,
                                                    buildFilters({ ...parsed, columns: e.target.value })
                                                ),
                                            })
                                        }
                                    >
                                        {columnOptions.map(nombre => (
                                            <MenuItem key={nombre} value={nombre}>
                                                {nombre}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        disabled={!collection}
                                        label={i18n.t("promptModal.form.herramientas.columns")}
                                        placeholder="text, metadata"
                                        slotProps={infoSlot(i18n.t("promptModal.form.herramientas.columnsNoList"))}
                                        value={parsed.columns.join(", ")}
                                        onChange={e =>
                                            onChange({
                                                path: joinPath(
                                                    collection,
                                                    buildFilters({
                                                        ...parsed,
                                                        columns: e.target.value
                                                            .split(",")
                                                            .map(c => c.trim())
                                                            .filter(Boolean),
                                                    })
                                                ),
                                            })
                                        }
                                    />
                                )}

                                <TextField
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    disabled={!collection}
                                    label={i18n.t("promptModal.form.herramientas.limit")}
                                    style={{ minWidth: 150 }}
                                    inputProps={{ min: 1, max: 100 }}
                                    value={parsed.limit}
                                    onChange={e =>
                                        onChange({
                                            path: joinPath(
                                                collection,
                                                buildFilters({ ...parsed, limit: e.target.value })
                                            ),
                                        })
                                    }
                                />
                            </div>
                        </>
                    )}

                    {isMongo && (
                        <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={10}
                            className={classes.sqlEditor}
                            label={i18n.t("promptModal.form.herramientas.filter")}
                            placeholder={"{\"phone\": \":telefono\"}"}
                            slotProps={infoSlot(i18n.t("promptModal.form.herramientas.filterHelp"))}
                            value={tool.query || ""}
                            onChange={e => onChange({ query: e.target.value })}
                        />
                    )}
                </>
            ) : (
                <div className={classes.filterRow}>
                    <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        label={i18n.t("promptModal.form.herramientas.url")}
                        placeholder="https://..."
                        value={tool.url || ""}
                        onChange={e => onChange({ url: e.target.value })}
                    />
                    <TextField
                        select
                        size="small"
                        variant="outlined"
                        label={i18n.t("promptModal.form.herramientas.metodo")}
                        value={tool.method || "POST"}
                        onChange={e => onChange({ method: e.target.value })}
                    >
                        <MenuItem value="POST">POST · cuerpo</MenuItem>
                        <MenuItem value="GET">GET · en la URL</MenuItem>
                    </TextField>
                </div>
            )}

            <div className={classes.toolSubsection}>
                <Typography className={classes.toolSubsectionTitle}>
                    {i18n.t("promptModal.form.herramientas.headers")}
                    <InfoTip
                        text={
                            kind === "database"
                                ? i18n.t("promptModal.form.herramientas.headersConConexion")
                                : i18n.t("promptModal.form.herramientas.headersAyuda")
                        }
                    />
                </Typography>
                <ToolHeadersEditor value={tool.headers} onChange={headers => onChange({ headers })} />
            </div>
        </div>
    );
};

const InfoTip = ({ text, warning }) => (
    <Tooltip title={text} arrow placement="top">
        <InfoOutlined
            fontSize="small"
            sx={{ color: warning ? "warning.main" : "text.disabled", cursor: "help", fontSize: 18 }}
        />
    </Tooltip>
);

const infoSlot = (text, { select, warning, ...slots } = {}) => ({
    ...slots,
    ...(select ? { select } : {}),
    input: {
        ...(slots.input || {}),
        endAdornment: (
            <InputAdornment position="end" sx={{ mr: select ? 2.5 : 0, alignSelf: "center" }}>
                <InfoTip text={text} warning={warning} />
            </InputAdornment>
        ),
    },
});

const parameterSource = (parameter) =>
    parameter.autoFill || (parameter.fixedMode || parameter.fixedValue ? "fixed" : "");

const ParametersEditor = ({ parameters, onChange, classes }) => {
    const updateRow = (idx, changes) =>
        onChange(parameters.map((p, i) => (i === idx ? { ...p, ...changes } : p)));

    const changeSource = (idx, source) =>
        updateRow(idx, {
            autoFill: source === "fixed" ? "" : source,
            fixedMode: source === "fixed",
            fixedValue: source === "fixed" ? parameters[idx].fixedValue || "" : "",
        });

    return (
        <div className={classes.parametersBox}>
            {parameters.length === 0 ? (
                <Typography className={classes.toolEmpty}>
                    {i18n.t("promptModal.form.herramientas.noData")}
                </Typography>
            ) : (
                <Table size="small" className={classes.paramTable}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{i18n.t("promptModal.form.herramientas.colField")}</TableCell>
                            <TableCell>{i18n.t("promptModal.form.herramientas.colSource")}</TableCell>
                            <TableCell>{i18n.t("promptModal.form.herramientas.parametroFormatoLabel")}</TableCell>
                            <TableCell style={{ width: 48 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {parameters.map((p, idx) => {
                            const source = parameterSource(p);
                            return (
                                <React.Fragment key={idx}>
                                <TableRow className={classes.paramMainRow}>
                                    <TableCell>
                                        <TextField
                                            placeholder={i18n.t("promptModal.form.herramientas.parametroNombrePlaceholder")}
                                            size="small"
                                            fullWidth
                                            value={p.nombre}
                                            onChange={e => updateRow(idx, { nombre: e.target.value })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            select
                                            size="small"
                                            fullWidth
                                            value={source}
                                            onChange={e => changeSource(idx, e.target.value)}
                                            slotProps={{ select: { displayEmpty: true } }}
                                        >
                                            <MenuItem value="">{i18n.t("promptModal.form.herramientas.sourceAgent")}</MenuItem>
                                            <MenuItem value="contactPhone">{i18n.t("promptModal.form.herramientas.parametroAutoFillTelefono")}</MenuItem>
                                            <MenuItem value="contactName">{i18n.t("promptModal.form.herramientas.parametroAutoFillNombre")}</MenuItem>
                                            <MenuItem value="fixed">{i18n.t("promptModal.form.herramientas.sourceFixed")}</MenuItem>
                                        </TextField>
                                    </TableCell>
                                    <TableCell>
                                        {source ? (
                                            <Typography className={classes.paramAuto}>—</Typography>
                                        ) : (
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                value={p.format || ""}
                                                onChange={e => updateRow(idx, { format: e.target.value })}
                                                slotProps={{ select: { displayEmpty: true } }}
                                            >
                                                {PARAMETER_FORMATS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => onChange(parameters.filter((_, i) => i !== idx))}>
                                            <DeleteOutlined fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                <TableRow className={classes.paramDetailRow}>
                                    <TableCell colSpan={4}>
                                        {source === "fixed" ? (
                                            <TextField
                                                placeholder={i18n.t("promptModal.form.herramientas.parametroValorFijoPlaceholder")}
                                                size="small"
                                                fullWidth
                                                value={p.fixedValue || ""}
                                                onChange={e => updateRow(idx, { fixedValue: e.target.value })}
                                            />
                                        ) : source ? (
                                            <Typography className={classes.paramAuto}>
                                                {i18n.t("promptModal.form.herramientas.sourceAutomatic")}
                                            </Typography>
                                        ) : (
                                            <TextField
                                                placeholder={i18n.t("promptModal.form.herramientas.parametroDescripcionPlaceholder")}
                                                size="small"
                                                fullWidth
                                                multiline
                                                value={p.descripcion}
                                                onChange={e => updateRow(idx, { descripcion: e.target.value })}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <Button
                size="small"
                startIcon={<AddOutlined fontSize="small" />}
                style={{ alignSelf: "flex-start" }}
                onClick={() => onChange([...parameters, { nombre: "", descripcion: "", format: "", fixedValue: "", autoFill: "" }])}
            >
                {i18n.t("promptModal.form.herramientas.agregarParametro")}
            </Button>
        </div>
    );
};

const ToolSection = ({ classes, number, title, help, children }) => (
    <section>
        <div className={classes.toolSectionHeader}>
            <span className={classes.toolSectionNumber}>{number}</span>
            <Typography className={classes.toolSectionTitle}>
                {title}
                {help && <InfoTip text={help} />}
            </Typography>
        </div>
        <div className={classes.toolSectionBody}>{children}</div>
    </section>
);

const DraftWatcher = ({ value, onChange }) => {
    useEffect(() => {
        onChange(value || "");
    }, [value, onChange]);
    return null;
};

const SettingRow = ({ classes, title, help, children }) => (
    <div className={classes.settingRow}>
        <div>
            <Typography className={classes.settingTitle}>{title}</Typography>
            {help && <Typography className={classes.settingHelp}>{help}</Typography>}
        </div>
        <div className={classes.settingControl}>{children}</div>
    </div>
);

const SETTINGS_FIELDS = ["name", "apiKey", "maxTokens", "temperature", "maxMessages", "queueId", "voice"];
const PROMPT_TAB = 0;
const KNOWLEDGE_TAB = 5;
const SETTINGS_TAB = 4;

const ErrorTabSwitcher = ({ submitCount, errors, onSwitch, nameInHeader }) => {
    useEffect(() => {
        if (!submitCount) return;
        const settingsFields = nameInHeader ? SETTINGS_FIELDS.filter(field => field !== "name") : SETTINGS_FIELDS;
        if (errors.prompt) {
            onSwitch(PROMPT_TAB);
        } else if (settingsFields.some(field => errors[field])) {
            onSwitch(SETTINGS_TAB);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount]);
    return null;
};

const InlineShell = ({ className, children }) => (
    <Paper variant="outlined" className={className}>
        {children}
    </Paper>
);

const PromptModal = ({ open, onClose, promptId, defaultType = "text", inline = false, onSaved }) => {
    const navigate = useNavigate();

    const PromptSchema = Yup.object().shape({
        name: Yup.string()
            .min(5, i18n.t("promptModal.form.validacion.nombreCorto"))
            .max(100, i18n.t("promptModal.form.validacion.nombreLargo"))
            .required(i18n.t("promptModal.form.validacion.requerido")),
        voice: Yup.string().required(i18n.t("promptModal.form.validacion.requerido")),
        apiKey: Yup.string().required(i18n.t("promptModal.form.validacion.requerido")),
        maxTokens: Yup.number().required(i18n.t("promptModal.form.validacion.requerido")),
        temperature: Yup.number().required(i18n.t("promptModal.form.validacion.requerido")),
        maxMessages: Yup.number().required(i18n.t("promptModal.form.validacion.requerido")),
        queueId: Yup.number()
            .nullable()
            .when("type", {
                is: val => val === "text",
                then: schema => schema.required(i18n.t("promptModal.form.validacion.colaRequerida")),
                otherwise: schema => schema.notRequired(),
            }),
    });

    const { classes } = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [model, setModel] = useState("");
    const [showApiKey, setShowApiKey] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [promptExpanded, setPromptExpanded] = useState(false);
    const [builderOpen, setBuilderOpen] = useState(false);
    const inlinePromptRef = useRef(null);
    const expandedPromptRef = useRef(null);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        voiceAccent: "",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        type: "text",
        triggerDescription: "",
    };

    const [prompt, setPrompt] = useState(initialState);
    const [draftPrompt, setDraftPrompt] = useState("");
    const [versionNote, setVersionNote] = useState("");
    const [currentVersion, setCurrentVersion] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const formikRef = useRef(null);

    const applyCoachDraft = (newPrompt, note) => {
        formikRef.current?.setFieldValue("prompt", newPrompt);
        if (note) setVersionNote(previous => (previous ? `${previous}; ${note}` : note).slice(0, 480));
        setTabValue(PROMPT_TAB);
    };

    const loadCurrentVersion = async () => {
        if (!inline || !promptId) {
            setCurrentVersion(null);
            return;
        }
        try {
            const { data } = await api.get(`/prompt/${promptId}/versions`);
            setCurrentVersion((data || []).find(version => version.current)?.version ?? null);
        } catch {
            setCurrentVersion(null);
        }
    };

    useEffect(() => {
        loadCurrentVersion();
        setVersionNote("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promptId, inline]);

    const [prompts, setPrompts] = useState([]);
    const [especialistaSeleccionado, setEspecialistaSeleccionado] = useState("");
    const [triggerNuevo, setTriggerNuevo] = useState("");
    const [guardandoEspecialista, setGuardandoEspecialista] = useState(false);
    const [editandoHijoId, setEditandoHijoId] = useState(null);
    const [hijoEditTexto, setHijoEditTexto] = useState("");

    const [herramientas, setHerramientas] = useState([]);
    const [dataSources, setDataSources] = useState([]);
    const [guardandoHerramienta, setGuardandoHerramienta] = useState(false);
    const [editingTool, setEditingTool] = useState(null);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt({ ...initialState, type: defaultType });
                setModel("");
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data, voiceAccent: data.voiceAccent || "" };
                });
                setSelectedVoice(data.voice);
                setModel(data.model || "");
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/prompt");
                setPrompts(data?.prompts || []);
            } catch (err) {
                toastError(err);
            }
        })();
    }, [open]);

    useEffect(() => {
        if (!open || !promptId) {
            setHerramientas([]);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/prompt/${promptId}/tools`);
                setHerramientas(data || []);
            } catch (err) {
                toastError(err);
            }
        })();
    }, [open, promptId]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/data-sources");
                setDataSources(data || []);
            } catch (err) {
                toastError(err);
            }
        })();
    }, [open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        setTabValue(0);
        setEspecialistaSeleccionado("");
        setTriggerNuevo("");
        setEditandoHijoId(null);
        setHijoEditTexto("");
        setHerramientas([]);
        setEditingTool(null);
        onClose?.();
    };

    const [typeTooltipOpen, setTypeTooltipOpen] = useState(false);
    const [typeMenuOpen, setTypeMenuOpen] = useState(false);

    const toggleActive = async () => {
        const active = prompt.active === false;
        try {
            await api.put(`/prompt/${promptId}/active`, { active });
            setPrompt(prevState => ({ ...prevState, active }));
            onSaved?.(promptId);
        } catch (err) {
            toastError(err);
        }
    };

    const recargarPrompt = async () => {
        if (!promptId) return;
        try {
            const { data } = await api.get(`/prompt/${promptId}`);
            setPrompt(prevState => ({ ...prevState, ...data }));
        } catch (err) {
            toastError(err);
        }
    };

    const linkedSpecialistIds = () => (prompt.promptsHijos || []).map(hijo => hijo.id);

    const handleVincularEspecialista = async () => {
        if (!especialistaSeleccionado) return;
        setGuardandoEspecialista(true);
        try {
            await api.put(`/prompt/${promptId}`, {
                specialistIds: [...linkedSpecialistIds(), especialistaSeleccionado],
            });
            if (triggerNuevo.trim()) {
                await api.put(`/prompt/${especialistaSeleccionado}`, { triggerDescription: triggerNuevo });
            }
            toast.success(i18n.t("promptModal.form.especialistas.vinculado"));
            setEspecialistaSeleccionado("");
            setTriggerNuevo("");
            await recargarPrompt();
        } catch (err) {
            toastError(err);
        }
        setGuardandoEspecialista(false);
    };

    const handleEditarHijo = (hijo) => {
        setEditandoHijoId(hijo.id);
        setHijoEditTexto(hijo.triggerDescription || "");
    };

    const handleCancelarEdicionHijo = () => {
        setEditandoHijoId(null);
        setHijoEditTexto("");
    };

    const handleGuardarTriggerHijo = async (hijo) => {
        try {
            await api.put(`/prompt/${hijo.id}`, { triggerDescription: hijoEditTexto });
            toast.success(i18n.t("promptModal.form.herramientas.actualizada"));
            setEditandoHijoId(null);
            setHijoEditTexto("");
            await recargarPrompt();
        } catch (err) {
            toastError(err);
        }
    };

    const handleDesvincularEspecialista = async (hijo) => {
        try {
            await api.put(`/prompt/${promptId}`, {
                specialistIds: linkedSpecialistIds().filter(id => id !== hijo.id),
            });
            toast.success(i18n.t("promptModal.form.especialistas.desvinculado"));
            await recargarPrompt();
        } catch (err) {
            toastError(err);
        }
    };

    const recargarHerramientas = async () => {
        if (!promptId) return;
        try {
            const { data } = await api.get(`/prompt/${promptId}/tools`);
            setHerramientas(data || []);
        } catch (err) {
            toastError(err);
        }
    };

    const parametrosValidos = (parametros) =>
        (parametros || []).filter(p => p.nombre?.trim()).map(({ fixedMode, ...p }) => p);

    const EMPTY_TOOL = {
        nombre: "",
        descripcion: "",
        url: "",
        parametros: [],
        kind: null,
        method: "GET",
        headers: {},
        dataSourceId: "",
        path: "",
        query: "",
    };

    const openNewTool = () => setEditingTool({ ...EMPTY_TOOL });

    const handlePickCalendar = () => {
        setEditingTool(null);
        toast.info(
            "Google Calendar se activa solo para esta IA en cuanto tu empresa lo conecte en Configuración -- no hace falta agregarlo como herramienta manual."
        );
        onClose?.();
        navigate("/settings?tab=google-calendar");
    };

    const openTool = (herramienta) =>
        setEditingTool({
            id: herramienta.id,
            nombre: herramienta.nombre,
            descripcion: herramienta.descripcion,
            url: herramienta.url || "",
            parametros: herramienta.parametros || [],
            method: herramienta.method || "POST",
            headers: herramienta.headers || {},
            dataSourceId: herramienta.dataSourceId || "",
            path: herramienta.path || "",
            query: herramienta.query || "",
            vectorParam: herramienta.vectorParam || "",
            embeddingModel: herramienta.embeddingModel || "",
            kind: herramienta.dataSourceId ? "database" : "webhook",
        });

    const handleSaveTool = async () => {
        const h = editingTool;
        if (!h?.nombre || !h?.descripcion || !(h.dataSourceId || h.url)) return;

        setGuardandoHerramienta(true);
        try {
            const cuerpo = {
                nombre: h.nombre,
                descripcion: h.descripcion,
                url: h.url,
                parametros: parametrosValidos(h.parametros),
                method: h.method,
                headers: h.headers,
                dataSourceId: h.dataSourceId || null,
                query: h.query,
                path: h.path,
            };

            if (h.id) {
                await api.put(`/prompt/${promptId}/tools/${h.id}`, cuerpo);
                toast.success(i18n.t("promptModal.form.herramientas.actualizada"));
            } else {
                await api.post(`/prompt/${promptId}/tools`, cuerpo);
                toast.success(i18n.t("promptModal.form.herramientas.agregada"));
            }

            setEditingTool(null);
            await recargarHerramientas();
        } catch (err) {
            toastError(err);
        }
        setGuardandoHerramienta(false);
    };

    const handleBorrarHerramienta = async (herramienta) => {
        try {
            await api.delete(`/prompt/${promptId}/tools/${herramienta.id}`);
            toast.success(i18n.t("promptModal.form.herramientas.eliminada"));
            await recargarHerramientas();
        } catch (err) {
            toastError(err);
        }
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values, voice: selectedVoice, model, versionNote };
        let savedId = promptId;
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                const { data } = await api.post("/prompt", promptData);
                savedId = data?.id;
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
            if (inline) return;
        }
        if (inline) {
            setVersionNote("");
            onSaved?.(savedId);
            await recargarPrompt();
            await loadCurrentVersion();
            return;
        }
        handleClose();
    };

    const ShellComponent = inline ? InlineShell : Dialog;
    const shellProps = inline
        ? { className: classes.inlinePanel }
        : { open, onClose: handleClose, maxWidth: "lg", scroll: "paper", fullWidth: true };
    const editorRows = inline ? 30 : 22;
    const tabPanelClass = inline ? `${classes.tabPanel} ${classes.inlineTabPanel}` : classes.tabPanel;

    return (
        <div className={inline ? classes.inlineRoot : classes.root}>
            <ShellComponent {...shellProps}>
                {!inline && (
                    <DialogTitle id="form-dialog-title">
                        {promptId
                            ? `${i18n.t("promptModal.title.edit")}`
                            : `${i18n.t("promptModal.title.add")}`}
                    </DialogTitle>
                )}
                <Formik
                    innerRef={formikRef}
                    initialValues={prompt}
                    enableReinitialize={true}
                    validationSchema={PromptSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, resetForm, submitCount, setFieldValue }) => (
                        <Form style={{ width: "100%" }}>
                            {inline && <DraftWatcher value={values.prompt} onChange={setDraftPrompt} />}
                            <ErrorTabSwitcher submitCount={submitCount} errors={errors} onSwitch={setTabValue} nameInHeader={inline} />
                            {}
                            <Dialog
                                open={promptExpanded}
                                onClose={() => setPromptExpanded(false)}
                                maxWidth="xl"
                                fullWidth
                            >
                                <DialogTitle>{i18n.t("promptModal.form.prompt")}</DialogTitle>
                                <DialogContent dividers>
                                    <div className={classes.promptSplit}>
                                        <div className={classes.promptEditorRow}>
                                            <span className={classes.promptPaneLabel}>
                                                {i18n.t("promptModal.form.promptEditorLabel")}
                                            </span>
                                            <div className={classes.promptEditorRow}>
                                                <Field
                                                    as={TextField}
                                                    name="prompt"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    minRows={26}
                                                    maxRows={26}
                                                    autoFocus
                                                    className={classes.promptEditor}
                                                    onKeyDown={insertTab}
                                                    inputRef={expandedPromptRef}
                                                />
                                                <PromptLineNumbers
                                                    value={values.prompt}
                                                    textareaRef={expandedPromptRef}
                                                    className={classes.promptLineNumbers}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span className={classes.promptPaneLabel}>
                                                {i18n.t("promptModal.form.promptPreviewLabel")}
                                            </span>
                                            <div className={classes.promptPreviewCell} style={{ flex: 1 }}>
                                                <div className={classes.promptPreview}>
                                                    {values.prompt?.trim() ? (
                                                        <Markdown options={{ forceBlock: true }}>{values.prompt}</Markdown>
                                                    ) : (
                                                        <span className={classes.promptPreviewEmpty}>
                                                            {i18n.t("promptModal.form.promptPreviewEmpty")}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <span className={classes.promptEditorCount} style={{ marginRight: "auto", paddingLeft: 16 }}>
                                        {countText(values.prompt)}
                                    </span>
                                    <Button onClick={() => setPromptExpanded(false)} color="primary" variant="contained">
                                        Listo
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {}
                            <Dialog
                                open={Boolean(editingTool)}
                                onClose={() => setEditingTool(null)}
                                maxWidth="lg"
                                fullWidth
                            >
                                <DialogTitle className={classes.toolTitle}>
                                    <span>
                                        {editingTool?.id
                                            ? i18n.t("promptModal.form.herramientas.editarTitulo")
                                            : i18n.t("promptModal.form.herramientas.nuevaTitulo")}
                                    </span>
                                    {}
                                    {editingTool?.kind && (
                                        <span className={classes.toolTitleSource}>
                                            {sourceLabel(editingTool, dataSources)}
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    setEditingTool(h => ({ ...h, kind: null }))
                                                }
                                            >
                                                {i18n.t("promptModal.form.herramientas.cambiarOrigen")}
                                            </Button>
                                        </span>
                                    )}
                                </DialogTitle>
                                <DialogContent dividers>
                                    {}
                                    {!editingTool?.kind ? (
                                        <SourcePicker
                                            classes={classes}
                                            dataSources={dataSources}
                                            onPick={eleccion =>
                                                setEditingTool(h => ({ ...h, ...eleccion }))
                                            }
                                            onPickCalendar={handlePickCalendar}
                                        />
                                    ) : (
                                    <div className={classes.toolForm}>
                                        <ToolSection
                                            classes={classes}
                                            number={1}
                                            title={i18n.t("promptModal.form.herramientas.sectionWhatItDoes")}
                                            help={i18n.t("promptModal.form.herramientas.sectionWhatItDoesHelp")}
                                        >
                                        <TextField
                                            label={i18n.t("promptModal.form.herramientas.nombre")}
                                            placeholder={i18n.t("promptModal.form.herramientas.nombrePlaceholder")}
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            autoFocus
                                            value={editingTool?.nombre || ""}
                                            onChange={e =>
                                                setEditingTool(h => ({ ...h, nombre: e.target.value }))
                                            }
                                        />
                                        <TextField
                                            label={i18n.t("promptModal.form.herramientas.descripcion")}
                                            placeholder={i18n.t("promptModal.form.herramientas.descripcionPlaceholder")}
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            value={editingTool?.descripcion || ""}
                                            onChange={e =>
                                                setEditingTool(h => ({ ...h, descripcion: e.target.value }))
                                            }
                                        />
                                        </ToolSection>
                                        <ToolSection
                                            classes={classes}
                                            number={2}
                                            title={i18n.t("promptModal.form.herramientas.sectionWhere")}
                                        >
                                            <SourceEditor
                                                classes={classes}
                                                tool={editingTool || {}}
                                                dataSources={dataSources}
                                                onChange={change =>
                                                    setEditingTool(h => ({ ...h, ...change }))
                                                }
                                            />
                                        </ToolSection>
                                        <ToolSection
                                            classes={classes}
                                            number={3}
                                            title={i18n.t("promptModal.form.herramientas.parametros")}
                                            help={i18n.t("promptModal.form.herramientas.sectionDataHelp")}
                                        >
                                            <ParametersEditor
                                                classes={classes}
                                                parameters={editingTool?.parametros || []}
                                                onChange={parameters =>
                                                    setEditingTool(h => ({ ...h, parametros: parameters }))
                                                }
                                            />
                                        </ToolSection>
                                    </div>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setEditingTool(null)} color="secondary">
                                        {i18n.t("promptModal.form.herramientas.cancelar")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveTool}
                                        disabled={
                                            guardandoHerramienta ||
                                            !editingTool?.nombre ||
                                            !editingTool?.descripcion ||
                                            !(editingTool?.dataSourceId || editingTool?.url)
                                        }
                                    >
                                        {i18n.t("promptModal.form.herramientas.guardar")}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {inline && (
                                <div className={classes.inlineHeader}>
                                    <Field
                                        as={TextField}
                                        name="name"
                                        variant="standard"
                                        placeholder={i18n.t("promptModal.form.namePlaceholder")}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        className={classes.inlineName}
                                        slotProps={{ input: { disableUnderline: true } }}
                                        fullWidth
                                    />
                                    {promptId && values.type === "specialist" && (
                                        <Tooltip title={i18n.t("prompts.activeHelp")}>
                                            <label className={`${classes.activeToggle} ${prompt.active === false ? classes.activeToggleOff : ""}`}>
                                                {i18n.t(prompt.active === false ? "prompts.inactive" : "prompts.active")}
                                                <Switch size="small" color="success" checked={prompt.active !== false} onChange={toggleActive} />
                                            </label>
                                        </Tooltip>
                                    )}
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<AutoAwesomeOutlined fontSize="small" />}
                                        onClick={() => setBuilderOpen(true)}
                                        style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                    >
                                        {i18n.t(values.prompt?.trim() ? "promptBuilder.improveButton" : "promptBuilder.createButton")}
                                    </Button>
                                    {promptId && (
                                        <Tooltip title={i18n.t("promptVersions.open")}>
                                            <Chip
                                                variant="outlined"
                                                icon={<HistoryOutlined fontSize="small" />}
                                                label={currentVersion ? `v${currentVersion}` : i18n.t("promptVersions.history")}
                                                onClick={() => setHistoryOpen(true)}
                                                className={classes.versionChip}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title={i18n.t(`prompts.typeHelp.${values.type || "text"}`)}
                                        placement="bottom-end"
                                        open={typeTooltipOpen && !typeMenuOpen}
                                        onOpen={() => setTypeTooltipOpen(true)}
                                        onClose={() => setTypeTooltipOpen(false)}
                                    >
                                        <Field
                                            as={Select}
                                            name="type"
                                            onOpen={() => setTypeMenuOpen(true)}
                                            onClose={() => setTypeMenuOpen(false)}
                                            variant="standard"
                                            disableUnderline
                                            className={classes.inlineType}
                                            renderValue={value => `${i18n.t("promptModal.form.type")}: ${i18n.t(`prompts.types.${value || "text"}`)}`}
                                        >
                                            {PROMPT_TYPES.map(type => (
                                                <MenuItem key={type} value={type}>
                                                    {i18n.t(`prompts.types.${type}`)}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Tooltip>
                                </div>
                            )}
                            <DialogContent dividers>
                                <Tabs
                                    value={tabValue}
                                    onChange={(e, value) => setTabValue(value)}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant="scrollable"
                                    scrollButtons={false}
                                    className={classes.tabs}
                                >
                                    <Tab
                                        value={PROMPT_TAB}
                                        icon={<NotesOutlined fontSize="small" />}
                                        iconPosition="start"
                                        label={i18n.t("promptModal.form.tabPrompt")}
                                    />
                                    <Tab
                                        value={SETTINGS_TAB}
                                        icon={<SettingsOutlined fontSize="small" />}
                                        iconPosition="start"
                                        label={i18n.t("promptModal.form.tabSettings")}
                                    />
                                    <Tab
                                        value={2}
                                        icon={<AccountTreeOutlined fontSize="small" />}
                                        iconPosition="start"
                                        label={i18n.t("promptModal.form.tabEspecialistas")}
                                        disabled={!promptId}
                                    />
                                    <Tab
                                        value={3}
                                        icon={<BuildOutlined fontSize="small" />}
                                        iconPosition="start"
                                        label={i18n.t("promptModal.form.tabHerramientas")}
                                        disabled={!promptId}
                                    />
                                    <Tab
                                        value={KNOWLEDGE_TAB}
                                        icon={<MenuBookOutlined fontSize="small" />}
                                        iconPosition="start"
                                        label={i18n.t("knowledge.promptTab.tab")}
                                        disabled={!promptId}
                                    />
                                </Tabs>

                                <div className={inline ? `${tabPanelClass} ${classes.inlineBasicTab}` : tabPanelClass} hidden={tabValue !== PROMPT_TAB}>
                                    <div className={inline ? classes.inlineFill : undefined}>
                                        <div className={inline ? `${classes.promptConIndice} ${classes.inlineFill}` : classes.promptConIndice}>
                                            <div className={inline ? `${classes.promptEditorColumna} ${classes.inlineFill}` : classes.promptEditorColumna}>
                                                <div className={inline ? `${classes.promptEditorRow} ${classes.inlineEditorRow}` : classes.promptEditorRow}>
                                                    <Field
                                                        as={TextField}
                                                        name="prompt"
                                                        error={touched.prompt && Boolean(errors.prompt)}
                                                        helperText={touched.prompt && errors.prompt}
                                                        variant="outlined"
                                                        margin="dense"
                                                        fullWidth
                                                        minRows={editorRows}
                                                        maxRows={editorRows}
                                                        multiline={true}
                                                        className={classes.promptEditor}
                                                        onKeyDown={insertTab}
                                                        inputRef={inlinePromptRef}
                                                    />
                                                    <PromptLineNumbers
                                                        value={values.prompt}
                                                        textareaRef={inlinePromptRef}
                                                        className={classes.promptLineNumbers}
                                                    />
                                                </div>
                                                <div className={classes.promptEditorBar}>
                                                    <span className={classes.promptEditorCount}>
                                                        {countText(values.prompt)}
                                                    </span>
                                                    <span style={{ display: "flex", gap: 8 }}>
                                                        {!inline && (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<AutoAwesomeOutlined fontSize="small" />}
                                                                onClick={() => setBuilderOpen(true)}
                                                            >
                                                                {i18n.t(values.prompt?.trim() ? "promptBuilder.improveButton" : "promptBuilder.createButton")}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            startIcon={<OpenInFullOutlined fontSize="small" />}
                                                            onClick={() => setPromptExpanded(true)}
                                                        >
                                                            Ampliar
                                                        </Button>
                                                    </span>
                                                    <PromptBuilderDialog
                                                        open={builderOpen}
                                                        onClose={() => setBuilderOpen(false)}
                                                        promptId={promptId}
                                                        type={values.type}
                                                        initialText={builderOpen ? values.prompt || "" : ""}
                                                        onApply={text => {
                                                            setFieldValue("prompt", text);
                                                            setBuilderOpen(false);
                                                            toast.success(i18n.t("promptBuilder.applied"));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={tabPanelClass} hidden={tabValue !== SETTINGS_TAB}>
                                    <div className={classes.settingsList}>
                                        {!inline && (
                                            <>
                                                <SettingRow classes={classes} title={i18n.t("promptModal.form.name")}>
                                                    <Field
                                                        as={TextField}
                                                        name="name"
                                                        error={touched.name && Boolean(errors.name)}
                                                        helperText={touched.name && errors.name}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                    />
                                                </SettingRow>
                                                <SettingRow
                                                    classes={classes}
                                                    title={i18n.t("promptModal.form.type")}
                                                    help={i18n.t(`prompts.typeHelp.${values.type || "text"}`)}
                                                >
                                                    <Field as={Select} name="type" variant="outlined" size="small" fullWidth>
                                                        {PROMPT_TYPES.map(type => (
                                                            <MenuItem key={type} value={type}>
                                                                {i18n.t(`prompts.types.${type}`)}
                                                            </MenuItem>
                                                        ))}
                                                    </Field>
                                                </SettingRow>
                                            </>
                                        )}

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.model")}
                                            help={i18n.t("promptModal.settings.modelHelp")}
                                        >
                                            <TextField
                                                select
                                                value={model}
                                                onChange={(e) => setModel(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                slotProps={{ select: { displayEmpty: true } }}
                                            >
                                                {TEXT_MODELS.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </SettingRow>

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.apiKey")}
                                            help={i18n.t("promptModal.settings.apiKeyHelp")}
                                        >
                                            <Field
                                                as={TextField}
                                                name="apiKey"
                                                type={showApiKey ? "text" : "password"}
                                                error={touched.apiKey && Boolean(errors.apiKey)}
                                                helperText={touched.apiKey && errors.apiKey}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={handleToggleApiKey} size="small" edge="end">
                                                                    {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </SettingRow>

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.accent")}
                                            help={i18n.t("promptModal.settings.accentHelp")}
                                        >
                                            <Field
                                                as={TextField}
                                                select
                                                name="voiceAccent"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                            >
                                                {VOICE_ACCENT_OPTIONS.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </SettingRow>

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.temperature")}
                                            help={i18n.t("promptModal.settings.temperatureHelp")}
                                        >
                                            <Field
                                                as={TextField}
                                                name="temperature"
                                                error={touched.temperature && Boolean(errors.temperature)}
                                                helperText={touched.temperature && errors.temperature}
                                                variant="outlined"
                                                size="small"
                                                className={classes.settingNumber}
                                            />
                                        </SettingRow>

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.maxTokens")}
                                            help={i18n.t("promptModal.settings.maxTokensHelp")}
                                        >
                                            <Field
                                                as={TextField}
                                                name="maxTokens"
                                                error={touched.maxTokens && Boolean(errors.maxTokens)}
                                                helperText={touched.maxTokens && errors.maxTokens}
                                                variant="outlined"
                                                size="small"
                                                className={classes.settingNumber}
                                            />
                                        </SettingRow>

                                        <SettingRow
                                            classes={classes}
                                            title={i18n.t("promptModal.settings.maxMessages")}
                                            help={i18n.t("promptModal.settings.maxMessagesHelp")}
                                        >
                                            <Field
                                                as={TextField}
                                                name="maxMessages"
                                                error={touched.maxMessages && Boolean(errors.maxMessages)}
                                                helperText={touched.maxMessages && errors.maxMessages}
                                                variant="outlined"
                                                size="small"
                                                className={classes.settingNumber}
                                            />
                                        </SettingRow>

                                        {values.type === "text" && (
                                            <SettingRow
                                                classes={classes}
                                                title={i18n.t("promptModal.settings.handoff")}
                                                help={i18n.t("promptModal.settings.handoffHelp")}
                                            >
                                                <div style={{ width: "100%" }}>
                                                    <QueueSelectSingle hideLabel />
                                                </div>
                                            </SettingRow>
                                        )}
                                    </div>
                                </div>

                                <div className={tabPanelClass} hidden={tabValue !== 2}>
                                    <Typography variant="caption" color="textSecondary" className={classes.sectionHelp}>
                                        {i18n.t("promptModal.form.especialistas.ayuda")}
                                    </Typography>

                                    {!promptId && (
                                        <Typography variant="body2" color="textSecondary">
                                            {i18n.t("promptModal.form.especialistas.guardarPrimero")}
                                        </Typography>
                                    )}

                                    {promptId && values.type === "specialist" && (
                                        <Typography variant="body2">
                                            {(values.parentPrompts || []).length > 0 ? (
                                                <>
                                                    {i18n.t("promptModal.form.especialistas.esEspecialistaDe")}{" "}
                                                    <b>{values.parentPrompts.map(parent => parent.name).join(", ")}</b>
                                                </>
                                            ) : (
                                                i18n.t("promptModal.form.especialistas.sinPadres")
                                            )}
                                        </Typography>
                                    )}

                                    {promptId && values.type !== "specialist" && (
                                        <>
                                            <Typography className={classes.grupo}>
                                                {i18n.t("promptModal.form.especialistas.vinculados")}
                                            </Typography>

                                            {(values.promptsHijos || []).length === 0 && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {i18n.t("promptModal.form.especialistas.sinEspecialistas")}
                                                </Typography>
                                            )}

                                            {(values.promptsHijos || []).map(hijo => (
                                                <Paper key={hijo.id} variant="outlined" className={classes.herramientaCard}>
                                                    {editandoHijoId === hijo.id ? (
                                                        <>
                                                            <TextField
                                                                label={i18n.t("promptModal.form.especialistas.cuandoUsarlo")}
                                                                placeholder={i18n.t(
                                                                    "promptModal.form.especialistas.cuandoUsarloPlaceholder"
                                                                )}
                                                                size="small"
                                                                variant="outlined"
                                                                fullWidth
                                                                multiline
                                                                minRows={2}
                                                                value={hijoEditTexto}
                                                                onChange={e => setHijoEditTexto(e.target.value)}
                                                            />
                                                            <div className={classes.multFieldLine}>
                                                                <Button size="small" onClick={handleCancelarEdicionHijo}>
                                                                    {i18n.t("promptModal.form.herramientas.cancelar")}
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleGuardarTriggerHijo(hijo)}
                                                                >
                                                                    {i18n.t("promptModal.form.herramientas.guardar")}
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className={classes.herramientaHeader}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Typography style={{ fontWeight: 600 }}>{hijo.name}</Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    style={{ display: "block" }}
                                                                >
                                                                    {hijo.triggerDescription ||
                                                                        i18n.t("promptModal.form.especialistas.cuandoUsarloPlaceholder")}
                                                                </Typography>
                                                            </div>
                                                            <IconButton size="small" onClick={() => handleEditarHijo(hijo)}>
                                                                <EditOutlined fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDesvincularEspecialista(hijo)}
                                                            >
                                                                <DeleteOutlined fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </Paper>
                                            ))}

                                            <Typography className={classes.grupo} style={{ marginTop: 8 }}>
                                                {i18n.t("promptModal.form.especialistas.vincularExistente")}
                                            </Typography>

                                            {(() => {
                                                const linkedIds = new Set((values.promptsHijos || []).map(hijo => hijo.id));
                                                const disponibles = prompts.filter(
                                                    p => p.type === "specialist" && p.id !== promptId && !linkedIds.has(p.id)
                                                );

                                                if (disponibles.length === 0) {
                                                    return (
                                                        <Typography variant="caption" color="textSecondary">
                                                            {i18n.t("promptModal.form.especialistas.sinDisponibles")}
                                                        </Typography>
                                                    );
                                                }

                                                return (
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "1fr 1fr auto",
                                                            gap: 8,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <FormControl size="small" fullWidth margin="dense" variant="outlined">
                                                            <InputLabel>
                                                                {i18n.t("promptModal.form.especialistas.elegirPrompt")}
                                                            </InputLabel>
                                                            <Select
                                                                size="small"
                                                                label={i18n.t("promptModal.form.especialistas.elegirPrompt")}
                                                                value={especialistaSeleccionado}
                                                                onChange={e => setEspecialistaSeleccionado(e.target.value)}
                                                            >
                                                                {disponibles.map(p => (
                                                                    <MenuItem key={p.id} value={p.id}>
                                                                        {p.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <TextField
                                                            label={i18n.t("promptModal.form.especialistas.cuandoUsarlo")}
                                                            placeholder={i18n.t(
                                                                "promptModal.form.especialistas.cuandoUsarloPlaceholder"
                                                            )}
                                                            size="small"
                                                            variant="outlined"
                                                            margin="dense"
                                                            fullWidth
                                                            value={triggerNuevo}
                                                            onChange={e => setTriggerNuevo(e.target.value)}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={handleVincularEspecialista}
                                                            disabled={!especialistaSeleccionado || guardandoEspecialista}
                                                        >
                                                            {i18n.t("promptModal.form.especialistas.vincular")}
                                                        </Button>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>

                                <div className={tabPanelClass} hidden={tabValue !== KNOWLEDGE_TAB}>
                                    {promptId && tabValue === KNOWLEDGE_TAB && (
                                        <PromptKnowledgeTab
                                            promptId={promptId}
                                            selectedIds={prompt.knowledgeDocumentIds}
                                            usage={prompt.knowledgeUsage}
                                            onSaved={async () => {
                                                await recargarPrompt();
                                                await recargarHerramientas();
                                            }}
                                        />
                                    )}
                                </div>

                                <div className={tabPanelClass} hidden={tabValue !== 3}>
                                    {!promptId ? (
                                        <Typography variant="body2" color="textSecondary">
                                            {i18n.t("promptModal.form.herramientas.guardarPrimero")}
                                        </Typography>
                                    ) : (
                                        <>
                                            <div className={classes.toolsBar}>
                                                <Typography className={classes.grupo}>
                                                    {i18n.t("promptModal.form.herramientas.configuradas")}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<AddOutlined fontSize="small" />}
                                                    onClick={openNewTool}
                                                >
                                                    {i18n.t("promptModal.form.herramientas.agregar")}
                                                </Button>
                                            </div>

                                            {herramientas.length === 0 ? (
                                                <Typography variant="caption" color="textSecondary">
                                                    {i18n.t("promptModal.form.herramientas.sinHerramientas")}
                                                </Typography>
                                            ) : (
                                                herramientas.map(herramienta => (
                                                    <Paper
                                                        key={herramienta.id}
                                                        variant="outlined"
                                                        className={classes.herramientaCard}
                                                    >
                                                        <div className={classes.herramientaHeader}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    {herramienta.nombre}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    style={{ display: "block" }}
                                                                >
                                                                    {herramienta.descripcion}
                                                                </Typography>
                                                                <Typography component="span" className={classes.herramientaUrl}>
                                                                    {herramienta.dataSource
                                                                        ? `${herramienta.dataSource.name} · ${herramienta.path || ""}`
                                                                        : herramienta.url}
                                                                </Typography>
                                                            </div>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openTool(herramienta)}
                                                            >
                                                                <EditOutlined fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleBorrarHerramienta(herramienta)}
                                                            >
                                                                <DeleteOutlined fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    </Paper>
                                                ))
                                            )}
                                        </>
                                    )}
                                </div>
                            </DialogContent>
                            <DialogActions>
                                {inline && promptId && values.prompt !== prompt.prompt && (
                                    <TextField
                                        size="small"
                                        value={versionNote}
                                        onChange={(e) => setVersionNote(e.target.value)}
                                        placeholder={i18n.t("promptVersions.notePlaceholder")}
                                        className={classes.versionNote}
                                    />
                                )}
                                <Button
                                    onClick={inline ? () => resetForm() : handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {inline ? i18n.t("promptModal.buttons.discard") : i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </ShellComponent>
            {inline && promptId && (
                <PromptVersionHistory
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    promptId={promptId}
                    currentText={prompt.prompt}
                    onRestored={async () => {
                        await recargarPrompt();
                        await loadCurrentVersion();
                        onSaved?.(promptId);
                    }}
                />
            )}
            {inline && (
                <PromptTestChat
                    onApplyDraft={applyCoachDraft}
                    onSpecialistSaved={() => onSaved?.(promptId)}
                    promptId={promptId}
                    promptType={prompt.type}
                    draftPrompt={draftPrompt}
                    savedPrompt={prompt.prompt}
                />
            )}
        </div>
    );
};

export default PromptModal;